import { FC, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useForm } from '@tanstack/react-form';
import { useStore } from '@tanstack/react-store';
import { SidePanel } from '@carbon/ibm-products';
import { ComboBox, InlineNotification, TextInput } from '@carbon/react';
import type { StagedWell, WellListItem } from './types';
import { editWellAtom } from './atoms/wells';
import wellStyles from './wells.module.scss';

// Formation / Basin typeahead hints (ported verbatim from the v2 mock
// WellEditPanel). Free text is still accepted via onInputChange — these are
// autocomplete suggestions only, and the typed/selected string round-trips to
// location_summary.{reported_formation, basin} exactly like the prior TextInput.
const FORMATION_OPTIONS = [
  'Wolfcamp A',
  'Wolfcamp B',
  'Wolfcamp C',
  'Wolfcamp D',
  'Spraberry',
  'Bone Spring',
  'Avalon',
  'Eagle Ford',
  'Austin Chalk',
  'Niobrara',
  'Codell',
  'Bakken',
  'Three Forks',
  'Haynesville',
  'Marcellus',
  'Utica',
];
const BASIN_OPTIONS = [
  'Permian (Midland)',
  'Permian (Delaware)',
  'Eagle Ford',
  'DJ',
  'Powder River',
  'Williston',
  'Anadarko',
  'Haynesville',
  'Marcellus',
  'Utica',
];

// Ported from the v2 mock `WellEditPanel`. Edits the staged well's fields and
// PATCHes them to `.../afe_structuring/wells/:id` (the `resources.update` href
// when present). The mock edits a flat field set; here those fields map onto the
// AfeTarget `data` store_accessor blobs that WellMatchable#edit_staged_fields!
// accepts (AfeTarget::EDITABLE_KEYS / the controller's EDIT_BLOB_KEYS):
//
//   Well Name            → well_name (scalar)
//   UWI / API Number     → identifiers.uwi
//   County / State       → location_summary.{county, state}
//   Formation            → location_summary.reported_formation
//   Basin                → location_summary.basin
//   TVD / TMD            → depths.{tvd, tmd}
//   Legal Description    → legal_descriptions[0].full_legal_description
//   SHL / BHL / FTP/ LTP → locations.{shl, bhl, ftp, ltp}
//   Quarter / Subdivision→ additional_header_fields.quarter_subdivision
//   Abstract / Survey    → additional_header_fields.abstract_survey
//   Prospect             → additional_header_fields.prospect
//
// WellMatchable#edit_staged_fields! deep-merges submitted leaves into `data`, so we
// send the full reconstructed hash for any key the user touched, and omit keys
// the user left entirely blank so we never clobber an extracted blob with an
// empty object.

interface FormState {
  wellName: string;
  uwiApi: string;
  county: string;
  state: string;
  formation: string;
  basin: string;
  tvd: string;
  tmd: string;
  tll: string;
  legalDescription: string;
  quarterSubdivision: string;
  shl: string;
  bhl: string;
  ftp: string;
  ltp: string;
  abstractSurvey: string;
  prospect: string;
}

interface Props {
  open: boolean;
  documentId: number | string;
  well: WellListItem;
  staged: StagedWell;
  /** PATCH target — prefer the HATEOAS href; fall back to the conventional URL. */
  editHref?: string;
  onClose: () => void;
  onSaved: () => void;
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());
const anyPresent = (...values: string[]) => values.some((v) => v.trim() !== '');

// Pre-fill only the fields the staged-well response actually carries.
// County/State arrive combined (`countyState`) and SHL/BHL/FTP/LTP/Quarter/
// Abstract/Prospect are not surfaced by the staged-well jbuilder yet, so those
// start blank until the backend response exposes them.
const buildFormValues = (staged: StagedWell): FormState => ({
  wellName: staged.wellName ?? '',
  uwiApi: staged.uwiApi ?? '',
  county: '',
  state: '',
  formation: staged.formation ?? '',
  basin: staged.basin ?? '',
  tvd: staged.tvd ?? '',
  tmd: staged.tmd ?? '',
  tll: staged.tll ?? '',
  legalDescription: staged.legalDescription ?? '',
  quarterSubdivision: '',
  shl: '',
  bhl: '',
  ftp: '',
  ltp: '',
  abstractSurvey: '',
  prospect: '',
});

export const WellEditPanel: FC<Props> = ({
  open,
  documentId,
  well,
  staged,
  editHref,
  onClose,
  onSaved,
}) => {
  const editWell = useSetAtom(editWellAtom);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: buildFormValues(staged),
    onSubmit: ({ value }) => {
      const url =
        editHref ?? `/documents/${documentId}/afe_structuring/wells/${well.id}`;

      // Reconstruct each `data` blob from the flat form. The backend deep-merges
      // these into `data`, so include a top-level key only when the user touched
      // one of its fields — untouched blobs are preserved by the merge, not replaced.
      const wellPayload: Record<string, unknown> = {
        well_name: value.wellName,
      };

      if (value.uwiApi.trim() !== '') {
        wellPayload.identifiers = { uwi: value.uwiApi.trim() };
      }

      if (anyPresent(value.county, value.state, value.basin, value.formation)) {
        wellPayload.location_summary = {
          county: blank(value.county),
          state: blank(value.state),
          basin: blank(value.basin),
          reported_formation: blank(value.formation),
        };
      }

      if (anyPresent(value.tvd, value.tmd, value.tll)) {
        wellPayload.depths = {
          tvd: blank(value.tvd),
          tmd: blank(value.tmd),
          tll: blank(value.tll),
        };
      }

      if (value.legalDescription.trim() !== '') {
        wellPayload.legal_descriptions = [
          { full_legal_description: value.legalDescription.trim() },
        ];
      }

      if (anyPresent(value.shl, value.bhl, value.ftp, value.ltp)) {
        wellPayload.locations = {
          shl: blank(value.shl),
          bhl: blank(value.bhl),
          ftp: blank(value.ftp),
          ltp: blank(value.ltp),
        };
      }

      if (
        anyPresent(
          value.quarterSubdivision,
          value.abstractSurvey,
          value.prospect
        )
      ) {
        wellPayload.additional_header_fields = {
          quarter_subdivision: blank(value.quarterSubdivision),
          abstract_survey: blank(value.abstractSurvey),
          prospect: blank(value.prospect),
        };
      }

      setPending(true);
      setError(null);
      editWell({
        url,
        method: 'PATCH',
        data: { well: wellPayload },
        onSuccess: () => {
          setPending(false);
          onSaved();
        },
        onError: (m) => {
          setPending(false);
          setError(m ?? 'Failed to save well.');
        },
      });
    },
  });

  // Re-seed the form whenever the panel (re)opens for a well — mirrors the prior
  // `[open, well.id]` pre-fill effect. The panel can stay mounted across wells
  // (well-review-match toggles `open`) or mount fresh per edit (wells-overview);
  // resetting on open re-baselines the form for both.
  useEffect(() => {
    if (open) {
      form.reset(buildFormValues(staged));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, well.id]);

  // Live well name drives the panel title (updates as the user edits the field).
  const wellNameValue = useStore(form.store, (s) => s.values.wellName);

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={wellNameValue || staged.wellName || 'Edit Well'}
      subtitle="Edit the staged well details extracted from the document."
      className={wellStyles.panelLightFields}
      slideIn
      selectorPageContent=".afe-structuring-shell"
      placement="right"
      size="md"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: pending ? 'Saving…' : 'Save Changes',
          disabled: pending,
          onClick: () => form.handleSubmit(),
        },
      ]}
    >
      {open && (
        <div className={wellStyles.editGrid}>
          {error && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
            />
          )}
          <form.Field name="wellName">
            {(field) => (
              <TextInput
                id="well-edit-name"
                labelText="Well Name"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="uwiApi">
            {(field) => (
              <TextInput
                id="well-edit-uwi-api"
                labelText="UWI / API Number"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="county">
            {(field) => (
              <TextInput
                id="well-edit-county"
                labelText="County"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="state">
            {(field) => (
              <TextInput
                id="well-edit-state"
                labelText="State"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="formation">
            {(field) => (
              <ComboBox
                id="well-edit-formation"
                titleText="Formation"
                placeholder="Search formations…"
                items={FORMATION_OPTIONS}
                selectedItem={field.state.value || null}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem?: string | null;
                }) => field.handleChange(selectedItem ?? '')}
                onInputChange={(value: string) =>
                  field.handleChange(value ?? '')
                }
                size="sm"
                light
              />
            )}
          </form.Field>
          <form.Field name="basin">
            {(field) => (
              <ComboBox
                id="well-edit-basin"
                titleText="Basin"
                placeholder="Search basins…"
                items={BASIN_OPTIONS}
                selectedItem={field.state.value || null}
                onChange={({
                  selectedItem,
                }: {
                  selectedItem?: string | null;
                }) => field.handleChange(selectedItem ?? '')}
                onInputChange={(value: string) =>
                  field.handleChange(value ?? '')
                }
                size="sm"
                light
              />
            )}
          </form.Field>
          <form.Field name="tvd">
            {(field) => (
              <TextInput
                id="well-edit-tvd"
                labelText="Planned True Vertical Depth"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="tmd">
            {(field) => (
              <TextInput
                id="well-edit-tmd"
                labelText="Planned True Measured Depth"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="tll">
            {(field) => (
              <TextInput
                id="well-edit-tll"
                labelText="Planned True Lateral Length"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="legalDescription">
            {(field) => (
              <TextInput
                id="well-edit-legal-description"
                labelText="Legal Description"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="quarterSubdivision">
            {(field) => (
              <TextInput
                id="well-edit-quarter"
                labelText="Quarter / Subdivision"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="shl">
            {(field) => (
              <TextInput
                id="well-edit-shl"
                labelText="SHL (Surface Hole Location)"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="bhl">
            {(field) => (
              <TextInput
                id="well-edit-bhl"
                labelText="BHL (Bottom Hole Location)"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="ftp">
            {(field) => (
              <TextInput
                id="well-edit-ftp"
                labelText="FTP (First Take Point)"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="ltp">
            {(field) => (
              <TextInput
                id="well-edit-ltp"
                labelText="LTP (Last Take Point)"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="abstractSurvey">
            {(field) => (
              <TextInput
                id="well-edit-abstract"
                labelText="Abstract / Survey"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
          <form.Field name="prospect">
            {(field) => (
              <TextInput
                id="well-edit-prospect"
                labelText="Prospect"
                size="sm"
                value={field.state.value}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.handleChange(e.target.value)
                }
                light
              />
            )}
          </form.Field>
        </div>
      )}
    </SidePanel>
  );
};
